import { ClientType, VendorType } from "@/interfaces";
import { Badge, Button, Modal, Table, Tag, Typography } from "antd";
import { Building2, Globe, MapPin, UserCheck } from "lucide-react";

const { Text, Paragraph } = Typography;

interface DuplicateSelectionModalProps {
  open: boolean;
  type: "client" | "vendor";
  items: (ClientType | VendorType)[];
  onSelect: (item: ClientType | VendorType) => void;
  onCancel: () => void;
  onProceedAnyway: () => void;
  isSaving?: boolean;
}

/**
 * Modal to display duplicate clients or vendors found during creation.
 * Allows the admin to select an existing one to map to their company instead of creating a duplicate.
 */
export default function DuplicateSelectionModal({
  open,
  type,
  items,
  onSelect,
  onCancel,
  onProceedAnyway,
  isSaving = false,
}: DuplicateSelectionModalProps) {
  const isClient = type === "client";
  const entityName = isClient ? "Client" : "Vendor";

  const columns = [
    {
      title: "Company Details",
      key: "details",
      render: (_: any, record: ClientType | VendorType) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-500" />
            <Text strong>{isClient ? (record as ClientType).companyName : (record as VendorType).vendorName}</Text>
          </div>
          {(record.domain || record.email) && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Globe className="w-3 h-3" />
              <span>{record.domain || record.email}</span>
            </div>
          )}
          {record.country && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <MapPin className="w-3 h-3" />
              <span>{record.country}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_: any, record: ClientType | VendorType) => (
        <div className="flex flex-col gap-2">
          {record.isActive ? (
            <Badge status="success" text="Active" />
          ) : (
            <Badge status="default" text="Inactive" />
          )}
        </div>
      ),
    },
    {
      title: "Current Associations",
      key: "associations",
      render: (_: any, record: ClientType | VendorType) => (
        <div className="flex flex-wrap gap-1">
          {record.companies && record.companies.length > 0 ? (
            record.companies.map((c) => (
              <Tag key={c.id} className="text-[10px] m-0">
                {c.companyName}
              </Tag>
            ))
          ) : (
            <Text type="secondary" italic className="text-xs">No companies</Text>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_: any, record: ClientType | VendorType) => (
        <Button
          type="primary"
          icon={<UserCheck className="w-4 h-4" />}
          size="small"
          onClick={() => onSelect(record)}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="proceed" type="dashed" onClick={onProceedAnyway} loading={isSaving}>
          Create New Anyway
        </Button>,
      ]}
      width={800}
      title={
        <div className="flex items-center gap-2">
          <Badge status="warning" />
          <span className="text-xl font-bold">Similar {entityName}s Found</span>
        </div>
      }
      centered
      className="duplicate-selection-modal"
    >
      <div className="mb-6 bg-amber-50 border border-amber-200 p-4 rounded-lg">
        <Paragraph className="m-0 text-amber-800">
          The following {entityName.toLowerCase()}s with similar names or domains already exist in the system but are <strong>not yet associated</strong> with your company.
        </Paragraph>
        <Paragraph className="m-0 mt-2 text-amber-900 font-medium">
          Would you like to select one of these instead of creating a new entry?
        </Paragraph>
      </div>

      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="middle"
        className="border rounded-md overflow-hidden"
        scroll={{ y: 300 }}
      />

      <div className="mt-6 text-center text-gray-500 text-sm">
        <Text type="secondary">
          If none of these are the correct {entityName.toLowerCase()}, you can click "Create New Anyway" below.
        </Text>
      </div>
    </Modal>
  );
}
